import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const leadPayload = (userId, company, details) => ({
  userId,
  companyName: company.companyName || "Not disclosed on profile",
  contactName: company.personName || null,
  contactTitle: company.personTitle || null,
  linkedinUrl: company.url?.includes("linkedin.com") ? company.url : (company.linkedinUrl || null),
  industry: company.industry || "",
  source: "autonomous_stream",
  sourceUrl: company.url,
  notes: company.snippet || company.title || "",
  email: company.email || company.contactEmail || null,
  contactEmail: company.email || company.contactEmail || null,
  website: company.companyDomain ? `https://${company.companyDomain}` : null,
  verification: company.verification || null,
  evidence: company.evidence || [],
  emailConfidence: company.emailConfidence || null,
  allEmails: company.allEmails || [],
  whyGoodFit: company.whyGoodFit || [],
  businessIntel: company.businessIntel || [],
  sources: company.sources || [],
  icpMatch: company.icpMatch || null,
  additionalInfo: details?.additionalInfo || [],
});

// Per-prospect actions inside the insight modal: save (with confirm), expand/enrich, outreach, copy, send.
export const useProspectActions = (userId, markInteracted) => {
  const [savingLead, setSavingLead] = useState(null);
  const [savedLeads, setSavedLeads] = useState(new Set());
  const [generatingOutreach, setGeneratingOutreach] = useState(null);
  const [outreachData, setOutreachData] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [leadDetails, setLeadDetails] = useState({});
  const [pendingSave, setPendingSave] = useState(null);

  const toggleExpand = async (idx, company) => {
    markInteracted();
    if (expandedLeads.has(idx)) {
      setExpandedLeads((prev) => { const next = new Set(prev); next.delete(idx); return next; });
      return;
    }
    setExpandedLeads((prev) => new Set([...prev, idx]));
    if (leadDetails[idx]) return;
    setLoadingDetails(idx);
    try {
      const query = company.personName ? `${company.personName} ${company.companyName || ""} background experience` : `${company.companyName || company.title} company overview`;
      const response = await axios.post(`${API}/search/web`, { query, numResults: 3 });
      if (response.data.results) {
        setLeadDetails((prev) => ({ ...prev, [idx]: { additionalInfo: response.data.results, fetchedAt: new Date().toISOString() } }));
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      setLeadDetails((prev) => ({ ...prev, [idx]: { additionalInfo: [], error: true } }));
    } finally {
      setLoadingDetails(null);
    }
  };

  const requestSave = (company, index) => {
    markInteracted();
    setPendingSave({ company, index });
  };

  const confirmSave = async () => {
    if (!pendingSave) return;
    const { company, index } = pendingSave;
    setPendingSave(null);
    setSavingLead(index);
    try {
      await axios.post(`${API}/leads`, leadPayload(userId, company, leadDetails[index]));
      setSavedLeads((prev) => new Set([...prev, index]));
      toast.success("Lead saved with full detail!");
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error("Failed to save lead");
    } finally {
      setSavingLead(null);
    }
  };

  const generateOutreach = async (company, index) => {
    markInteracted();
    setGeneratingOutreach(index);
    try {
      const response = await axios.post(`${API}/outreach/generate-outreach`, {
        userId,
        contactName: company.personName || "Decision Maker",
        contactTitle: company.personTitle || "Executive",
        companyName: company.companyName || company.title?.split(" at ")?.[1] || "Company",
        companyUrl: company.url,
        linkedinUrl: company.url?.includes("linkedin.com") ? company.url : null,
        triggerEvent: company.snippet || "ICP match",
      });
      if (response.data.success) {
        setOutreachData((prev) => ({ ...prev, [index]: response.data }));
        toast.success("Outreach generated!");
      }
    } catch (error) {
      console.error("Error generating outreach:", error);
      toast.error("Failed to generate outreach");
    } finally {
      setGeneratingOutreach(null);
    }
  };

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const sendEmail = async (outreach, company) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/outreach/send-email`, {
        userId,
        recipientEmail: outreach.email?.recipientEmail || "",
        recipientName: outreach.email?.recipientName || company.personName || "Prospect",
        subject: outreach.email?.subject || "Introduction",
        body: outreach.email?.body || "",
      });
      if (response.data.success) toast.success(response.data.message);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const cardProps = (idx) => ({
    isSaved: savedLeads.has(idx), isSaving: savingLead === idx, isExpanded: expandedLeads.has(idx), isLoadingDetails: loadingDetails === idx,
    details: leadDetails[idx], outreach: outreachData[idx], isGenerating: generatingOutreach === idx, sendingEmail, copiedField,
    onSaveClick: requestSave, onToggleExpand: toggleExpand, onGenerateOutreach: generateOutreach, onCopy: copy, onSendEmail: sendEmail,
  });

  return { pendingSave, setPendingSave, confirmSave, cardProps };
};

// Execute Strategy + inline reply to an agent question.
export const useStrategyExecution = (userId, activityData, onStrategyExecuted, markInteracted) => {
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [replyResult, setReplyResult] = useState(null);

  const execute = async () => {
    if (executing) return;
    setExecuting(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/execute-strategy`, {
        userId,
        strategyType: activityData.type,
        insights: activityData.insights,
        summary: activityData.summary,
        context: { hasQuestion: activityData.hasQuestion, actionId: activityData.actionId },
      });
      if (response.data.success) {
        setResults(response.data);
        toast.success(response.data.message || "Strategy executed successfully!");
        onStrategyExecuted?.();
      } else {
        toast.error(response.data.message || "Failed to execute strategy");
      }
    } catch (error) {
      console.error("Error executing strategy:", error);
      toast.error("Failed to execute strategy. Please try again.");
    } finally {
      setExecuting(false);
    }
  };

  const reply = async () => {
    if (!replyText.trim() || isReplying) return;
    setIsReplying(true);
    try {
      const response = await axios.post(`${API}/agent/respond-to-insight`, {
        userId,
        originalContent: activityData?.insights || activityData?.summary || "",
        response: replyText.trim(),
        activityType: activityData?.type || "strategy",
        activityId: activityData?.activityId,
      });
      if (response.data.success) {
        setReplyResult(response.data.followUp);
        toast.success("Response processed — updated strategy generated");
        markInteracted();
      }
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to process response. Please try again.");
    } finally {
      setIsReplying(false);
    }
  };

  const updateReplyText = (text) => {
    setReplyText(text);
    markInteracted();
  };

  return { executing, results, resetResults: () => setResults(null), execute, replyText, setReplyText: updateReplyText, isReplying, replyResult, reply };
};
